import * as React from "react";
import { cn } from "../../lib/utils";
import { Input } from "./input";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

const Autocomplete = React.forwardRef(({
  suggestions = [],
  value = "",
  onValueChange,
  placeholder = "Type to search...",
  emptyMessage = "No results found",
  className,
  inputClassName,
  disabled = false,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(value);
  const inputRef = React.useRef(null);

  // Sync input with external value
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filteredSuggestions = React.useMemo(() => {
    if (!inputValue) return suggestions.slice(0, 10);
    const search = inputValue.toLowerCase();
    return suggestions
      .filter(s => s.toLowerCase().includes(search))
      .slice(0, 10);
  }, [inputValue, suggestions]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onValueChange?.(newValue);
    if (!open && newValue) {
      setOpen(true);
    }
  };

  const handleSelect = (suggestion) => {
    setInputValue(suggestion);
    onValueChange?.(suggestion);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn("relative", className)}>
          <Input
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
            }}
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            className={inputClassName}
            onFocus={() => filteredSuggestions.length > 0 && setOpen(true)}
            {...props}
          />
        </div>
      </PopoverTrigger>
      {filteredSuggestions.length > 0 && (
        <PopoverContent 
          className="p-0 w-[--radix-popover-trigger-width]" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {filteredSuggestions.map((suggestion) => (
                  <CommandItem
                    key={suggestion}
                    value={suggestion}
                    onSelect={() => handleSelect(suggestion)}
                    className="cursor-pointer"
                  >
                    {suggestion}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
});

Autocomplete.displayName = "Autocomplete";

export { Autocomplete };
